/**
 * Banktransaktionen importieren und abgleichen.
 *
 * Alle drei Formate — CAMT.053, MT940, PDF — münden im Server in dieselbe
 * Struktur, deshalb sieht diese Seite für alle gleich aus. Die Herkunft steht
 * trotzdem an jeder Zeile: Ein aus einem PDF gelesener Umsatz ist weniger
 * belastbar als einer aus CAMT, und das soll man sehen.
 */

import { useEffect, useMemo, useState } from 'react';

import { Ablagezone } from '../komponenten/Ablagezone';
import { Gegenueberstellung } from './Gegenueberstellung';
import { Haken } from '../komponenten/Haken';
import { api, datum, datumKurz, eur } from '../lib/api';
import type {
  Banktransaktion,
  Beleg,
  PruefStatus,
  Zahlungsbefund,
  Zuordnung,
} from '../lib/typen';

type BankFilter = 'alle' | 'zugeordnet' | 'vorschlag' | 'ohne_beleg';

export interface BankProps {
  pruefungId: string;
  transaktionen: Banktransaktion[];
  zuordnungen: Zuordnung[];
  /** Position → Belege, die sie bezahlt. Sammelüberweisungen haben mehrere. */
  belegeJePosition: Record<string, string[]>;
  /** Abbuchungen ohne zugeordneten Beleg. */
  ohneBeleg: Set<string>;
  belege: Beleg[];
  darfHochladen: boolean;
  /**
   * Beim Sprung aus einem Beleg: diese Position wird ausgewählt und
   * ins Sichtfeld gerollt.
   */
  zielPosition: string | null;
  onZielErledigt: () => void;
  /** Zahlungsbefunde — die Gegenüberstellung liest daraus die Verknüpfungen. */
  zahlungen: Record<string, Zahlungsbefund>;
  /** Prüfstatus je Kontoauszugsposition. */
  transaktionStatus: Record<string, PruefStatus>;
  onTransaktionStatus: (transaktionId: string, status: PruefStatus) => void;
  onVerknuepfen: (transaktionId: string, belegId: string) => void;
  onZuordnung: (zuordnungId: string, was: 'bestaetigen' | 'loesen') => void;
  onNeuLaden: () => void;
  onBeleg: (belegId: string) => void;
  onRueckfrage: (transaktionId: string) => void;
  onMeldung: (text: string) => void;
  /** Ob für diese Prüfung bereits ein Journal eingespielt ist. */
  journalVorhanden: boolean;
  /** Wechsel in den Journalbereich — dort steht, was der Abgleich gefunden hat. */
  onJournal: () => void;
}

export function Bank({
  pruefungId,
  transaktionen,
  zuordnungen,
  belege,
  darfHochladen,
  onNeuLaden,
  belegeJePosition,
  ohneBeleg,
  zielPosition,
  onZielErledigt,
  zahlungen,
  transaktionStatus,
  onTransaktionStatus,
  onVerknuepfen,
  onZuordnung,
  onBeleg,
  onRueckfrage,
  onMeldung,
  journalVorhanden,
  onJournal,
}: BankProps) {
  // Zwei Arbeitsweisen. Die Gegenüberstellung beantwortet „wie hängt das
  // zusammen?", die Einzelansicht „was steckt hinter dieser Buchung?".
  // Voreingestellt ist die Gegenüberstellung: sie zeigt beim Öffnen mehr.
  const [ansicht, setAnsicht] = useState<'gegen' | 'einzeln'>('gegen');
  const [filter, setFilter] = useState<BankFilter>('alle');
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [hinweise, setHinweise] = useState<string[]>([]);

  const belegNach = useMemo(() => new Map(belege.map((b) => [b.id, b])), [belege]);

  // Sprung aus einem Beleg: die Position auswählen, Filter aufheben, damit
  // sie nicht hinter einem Filter versteckt liegt, und hinrollen. Ohne das
  // Zurücksetzen landet der Beirat auf einer leeren Liste und hält den
  // Sprung für kaputt.
  useEffect(() => {
    if (!zielPosition) return;
    // Der Sprung zeigt eine einzelne Position — also in die Einzelansicht,
    // sonst landet der Beirat in der Gegenüberstellung und sucht.
    setAnsicht('einzeln');
    setFilter('alle');
    setGewaehlt(zielPosition);
    const knoten = document.querySelector(`[data-tx="${zielPosition}"]`);
    knoten?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    onZielErledigt();
  }, [zielPosition, onZielErledigt]);

  /** Zuordnungen je Transaktion. Eine Sammelzahlung hat mehrere. */
  const jeTransaktion = useMemo(() => {
    const karte = new Map<string, Zuordnung[]>();
    for (const z of zuordnungen) {
      const liste = karte.get(z.transaktionId) ?? [];
      liste.push(z);
      karte.set(z.transaktionId, liste);
    }
    return karte;
  }, [zuordnungen]);

  function statusVon(t: Banktransaktion): BankFilter {
    const liste = jeTransaktion.get(t.id);
    if (!liste?.length) return 'ohne_beleg';
    return liste.every((z) => z.status === 'zugeordnet') ? 'zugeordnet' : 'vorschlag';
  }

  const gefiltert = useMemo(
    () => (filter === 'alle' ? transaktionen : transaktionen.filter((t) => statusVon(t) === filter)),
    [transaktionen, filter, jeTransaktion],
  );

  const aktuelle = gefiltert.find((t) => t.id === gewaehlt) ?? gefiltert[0] ?? null;

  const kennzahlen = useMemo(() => {
    const zugeordnet = transaktionen.filter((t) => statusVon(t) === 'zugeordnet').length;
    const ohne = transaktionen.filter((t) => statusVon(t) === 'ohne_beleg');
    return {
      gesamt: transaktionen.length,
      zugeordnet,
      offen: transaktionen.length - zugeordnet,
      nichtZugeordnet: ohne.reduce((s, t) => s + t.betrag, 0),
      quote: transaktionen.length ? zugeordnet / transaktionen.length : 0,
    };
  }, [transaktionen, jeTransaktion]);

  async function hochladen(dateien: File[]) {
    setLaeuft(true);
    try {
      const ergebnis = await api.bankHochladen(pruefungId, dateien);
      setHinweise(ergebnis.hinweise);
      onMeldung(
        `${ergebnis.neu} Buchungen übernommen` +
          (ergebnis.dubletten ? `, ${ergebnis.dubletten} bereits vorhanden` : '') +
          '.',
      );
      await api.bankAbgleichen(pruefungId);
      onNeuLaden();
    } catch (grund) {
      onMeldung((grund as Error).message);
    } finally {
      setLaeuft(false);
    }
  }

  async function neuAbgleichen() {
    setLaeuft(true);
    try {
      const ergebnis = await api.bankAbgleichen(pruefungId);
      onMeldung(
        `${ergebnis.kennzahlen.zugeordnet} zugeordnet, ${ergebnis.kennzahlen.vorschlaege} Vorschläge, ` +
          `${ergebnis.kennzahlen.sammelzahlungen} Sammelzahlungen aufgelöst.`,
      );
      onNeuLaden();
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <div className="wrap">
      {darfHochladen && (
        <div style={{ marginBottom: 16 }}>
          <Ablagezone
            mehrfach
            endungen={['.xml', '.sta', '.940', '.txt', '.csv', '.pdf']}
            titel="Kontoauszüge hier ablegen"
            hinweis="CAMT.053 (XML) · MT940 · CSV · PDF — das Format wird am Inhalt erkannt"
            onDateien={hochladen}
            kind={
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 10h18M5 10V7l7-4 7 4v3M5 10v8m4-8v8m6-8v8m4-8v8M3 21h18" />
              </svg>
            }
          />

          {/* Das Journal gehört nicht hierher, wird aber hier gesucht: Beides
              sind Unterlagen, die die Hausverwaltung beibringt. Statt den
              Nutzer suchen zu lassen, nimmt diese Fläche die Datei an und
              führt anschließend dorthin, wo das Ergebnis steht. */}
          <div
            className="card"
            style={{
              marginTop: 10,
              padding: '10px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
            }}
          >
            <span style={{ fontSize: 13 }}>
              {journalVorhanden
                ? 'Das Journal — die Buchungsliste der Hausverwaltung — ist eingespielt.'
                : 'Sie suchen das Journal? Die Buchungsliste des Jahres gehört in den eigenen Bereich.'}
            </span>
            <button className="btn ghost sm" style={{ marginLeft: 'auto' }} onClick={onJournal}>
              {journalVorhanden ? 'Zum Journal' : 'Journal einspielen'}
            </button>
          </div>
        </div>
      )}

      <div className="card statusband" style={{ gap: '16px 30px' }}>
        <div className="kpi">
          <div className="v">{kennzahlen.gesamt}</div>
          <div className="l">Transaktionen</div>
        </div>
        <div className="kpi">
          <div className="v ok">{kennzahlen.zugeordnet}</div>
          <div className="l">Zugeordnet · {Math.round(kennzahlen.quote * 100)} %</div>
        </div>
        <div className="kpi">
          <div className={`v${kennzahlen.offen ? ' w' : ''}`}>{kennzahlen.offen}</div>
          <div className="l">Offen</div>
        </div>
        <div className="kpi">
          <div className="v num">{eur(kennzahlen.nichtZugeordnet)}</div>
          <div className="l">Ohne Beleg</div>
        </div>
        <div className="spacer" />
        {darfHochladen && (
          <button className="btn ghost" onClick={neuAbgleichen} disabled={laeuft || !transaktionen.length}>
            {laeuft ? 'Läuft …' : 'Neu abgleichen'}
          </button>
        )}
      </div>

      {hinweise.length > 0 && (
        <div className="card" style={{ marginTop: 16, padding: '4px 16px' }}>
          {hinweise.map((h, i) => (
            <div key={i} className="check" style={{ fontSize: 12.5, color: 'var(--tx-2)' }}>
              {h}
            </div>
          ))}
        </div>
      )}

      <div
        className="chips"
        style={{ margin: '16px 0 10px', justifyContent: 'space-between' }}
      >
        <div className="chips" style={{ margin: 0 }}>
          {ansicht === 'einzeln' &&
            (
              [
                ['alle', 'Alle'],
                ['ohne_beleg', 'Ohne Beleg'],
                ['vorschlag', 'Vorschläge'],
                ['zugeordnet', 'Zugeordnet'],
              ] as [BankFilter, string][]
            ).map(([wert, text]) => (
              <button
                key={wert}
                className="fchip"
                aria-pressed={filter === wert}
                onClick={() => setFilter(wert)}
              >
                {text}
              </button>
            ))}
        </div>

        <div className="chips" style={{ margin: 0 }} role="group" aria-label="Ansicht">
          {(
            [
              ['gegen', 'Gegenüberstellung'],
              ['einzeln', 'Einzelansicht'],
            ] as ['gegen' | 'einzeln', string][]
          ).map(([wert, text]) => (
            <button
              key={wert}
              className="fchip"
              aria-pressed={ansicht === wert}
              onClick={() => setAnsicht(wert)}
            >
              {text}
            </button>
          ))}
        </div>
      </div>

      {transaktionen.length === 0 ? (
        <div className="leer card">
          <h3>Noch kein Kontoauszug eingespielt</h3>
          <p>
            Ohne Kontoauszug lässt sich nicht feststellen, ob und wann die Belege bezahlt wurden.
            Die Belegprüfung selbst ist davon nicht betroffen.
          </p>
        </div>
      ) : ansicht === 'gegen' ? (
        <Gegenueberstellung
          belege={belege}
          transaktionen={transaktionen}
          zahlungen={zahlungen}
          zuordnungen={zuordnungen}
          ohneBeleg={ohneBeleg}
          transaktionStatus={transaktionStatus}
          onBeleg={onBeleg}
          onRueckfrage={onRueckfrage}
          onTransaktionStatus={onTransaktionStatus}
          onVerknuepfen={onVerknuepfen}
          onZuordnung={onZuordnung}
        />
      ) : (
        <div className="banksplit">
          <div className="card txlist">
            {gefiltert.map((t) => {
              const status = statusVon(t);
              return (
                <button
                  key={t.id}
                  data-tx={t.id}
                  className={`tx ${status}${t.id === aktuelle?.id ? ' gewaehlt' : ''}`}
                  onClick={() => setGewaehlt(t.id)}
                >
                  <span className="d">{datumKurz(t.buchungsdatum)}</span>
                  <span className="p">
                    <b>{t.partnerName ?? 'Ohne Partner'}</b>
                    <small>{t.verwendungszweck ?? '—'}</small>
                  </span>
                  <span className={`a${t.richtung === 'eingang' ? ' pos' : ''}`}>
                    {t.richtung === 'eingang' ? '+' : '−'}
                    {eur(t.betrag)}
                  </span>
                  <Haken zustand={transaktionStatus[t.id] ?? 'offen'} />
                </button>
              );
            })}
            {gefiltert.length === 0 && (
              <div style={{ padding: 28, textAlign: 'center', color: 'var(--tx-2)', fontSize: 13 }}>
                Keine Transaktion in diesem Filter.
              </div>
            )}
          </div>

          <div className="card" style={{ padding: 16 }}>
            {aktuelle ? (
              <Abgleichbereich
                transaktion={aktuelle}
                zuordnungen={jeTransaktion.get(aktuelle.id) ?? []}
                belegIds={belegeJePosition[aktuelle.id] ?? []}
                ohneBeleg={ohneBeleg.has(aktuelle.id)}
                belegNach={belegNach}
                onBeleg={onBeleg}
                onRueckfrage={onRueckfrage}
              />
            ) : (
              <p style={{ margin: 0, color: 'var(--tx-2)', fontSize: 13 }}>
                Wählen Sie links eine Transaktion.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Woher eine Buchung stammt.
 *
 * Steht an jeder Zeile, weil die Formate unterschiedlich belastbar sind:
 * CAMT trägt strukturierte Felder, ein aus einem PDF oder freiem XML
 * gelesener Umsatz ist geraten. Das soll man sehen, bevor man darauf etwas
 * stützt.
 */
const HERKUNFT: Record<Banktransaktion['herkunft'], string> = {
  camt053: 'CAMT.053',
  camt052: 'CAMT.052 — Zwischensaldenbericht',
  camt054: 'CAMT.054 — Soll-/Habenanzeige',
  mt940: 'MT940',
  pdf: 'PDF — bitte stichprobenartig prüfen',
  idwell: 'aus iDWELL',
  csv: 'CSV — Spaltenzuordnung geraten, bitte prüfen',
  json: 'JSON',
  xml: 'XML — Feldzuordnung geraten, bitte prüfen',
};

function Abgleichbereich({
  transaktion,
  zuordnungen,
  belegIds,
  ohneBeleg,
  belegNach,
  onBeleg,
  onRueckfrage,
}: {
  transaktion: Banktransaktion;
  zuordnungen: Zuordnung[];
  /** Die Belege dieser Position — die Rückrichtung der Sprungnavigation. */
  belegIds: string[];
  /** true, wenn es eine Abbuchung ohne zugeordneten Beleg ist. */
  ohneBeleg: boolean;
  belegNach: Map<string, Beleg>;
  onBeleg: (belegId: string) => void;
  onRueckfrage: (transaktionId: string) => void;
}) {
  const sammel = zuordnungen.length > 1;
  const status = !zuordnungen.length
    ? { klasse: 'n', text: 'Ohne Beleg' }
    : zuordnungen.every((z) => z.status === 'zugeordnet')
      ? { klasse: 'ok', text: 'Zugeordnet' }
      : { klasse: 'w', text: 'Vorschlag' };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <span className="eyebrow">Abgleich</span>
        <span className={`pill ${status.klasse}`}>
          <span className="dot" />
          {status.text}
        </span>
      </div>

      <div style={{ marginTop: 10, fontSize: 13.5, fontWeight: 600 }}>
        {transaktion.partnerName ?? 'Ohne Partner'}
      </div>
      <div className="mono" style={{ fontSize: 11.5, color: 'var(--tx-2)' }}>
        {transaktion.verwendungszweck ?? '—'}
      </div>
      <div className="num" style={{ fontSize: 22, fontWeight: 600, marginTop: 6 }}>
        {transaktion.richtung === 'eingang' ? '+' : '−'}
        {eur(transaktion.betrag)}
      </div>
      <div style={{ fontSize: 12, color: 'var(--tx-3)' }}>
        Buchung {datum(transaktion.buchungsdatum)} · {transaktion.kontoIban ?? 'Konto unbekannt'} ·{' '}
        {HERKUNFT[transaktion.herkunft]}
      </div>

      {sammel && (
        <p className="note" style={{ marginTop: 12 }}>
          Sammelzahlung: Diese Überweisung deckt {zuordnungen.length} Belege. Die Teilbeträge führt
          dieses Modul selbst — die Verknüpfung in iDWELL kennt keinen Teilbetrag.
        </p>
      )}

      {zuordnungen.length === 0 ? (
        <>
          <p className="note" style={{ marginTop: 12 }}>
            {ohneBeleg
              ? 'Zu dieser Abbuchung liegt keine Rechnung vor. Das ist der Fall, für den ein Beirat da ist.'
              : transaktion.richtung === 'eingang'
                ? 'Ein Zahlungseingang — Vorschreibungen und Rücklagenzuführungen brauchen keinen Beleg.'
                : 'Zu dieser Zahlung ist kein Beleg zugeordnet.'}
          </p>
          {ohneBeleg && (
            <button
              className="btn warnline sm"
              style={{ marginTop: 10 }}
              onClick={() => onRueckfrage(transaktion.id)}
            >
              Rückfrage dazu stellen
            </button>
          )}
        </>
      ) : (
        <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {zuordnungen.map((z) => {
            const beleg = belegNach.get(z.belegId);
            return (
              <div
                key={z.id}
                className={beleg ? 'klickbar' : undefined}
                role={beleg ? 'button' : undefined}
                tabIndex={beleg ? 0 : undefined}
                title={beleg ? 'Diesen Beleg öffnen' : undefined}
                onClick={beleg ? () => onBeleg(beleg.id) : undefined}
                onKeyDown={
                  beleg
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onBeleg(beleg.id);
                        }
                      }
                    : undefined
                }
                style={{
                  border: '1px solid var(--line)',
                  borderRadius: 'var(--r-s)',
                  padding: '10px 12px',
                  display: 'flex',
                  gap: 10,
                  alignItems: 'flex-start',
                  cursor: beleg ? 'pointer' : undefined,
                }}
              >
                <div className="thumb" style={{ width: 26, height: 34 }} />
                <div style={{ flex: 1, minWidth: 0, fontSize: 12.5 }}>
                  <b>{beleg?.lieferant ?? 'Beleg nicht im Zwischenspeicher'}</b>
                  <small style={{ display: 'block', color: 'var(--tx-3)' }} className="mono">
                    {beleg?.buchungszeilen[0]?.sachkontoNummer ?? '—'} ·{' '}
                    {beleg?.rechnungsnummer ?? '—'}
                  </small>
                  <div className="num" style={{ marginTop: 4 }}>
                    {eur(z.betrag)}
                    {beleg && Math.abs(z.betrag - (beleg.brutto ?? 0)) > 0.02 && (
                      <span style={{ color: 'var(--tx-3)' }}> von {eur(beleg.brutto)}</span>
                    )}
                  </div>
                  {z.begruendung && (
                    <div style={{ fontSize: 11, color: 'var(--tx-3)', marginTop: 4 }}>
                      {z.begruendung}
                    </div>
                  )}
                  {beleg && (
                    <button
                      className="btn ghost sm"
                      style={{ marginTop: 8 }}
                      onClick={() => onBeleg(beleg.id)}
                    >
                      Im Split View öffnen
                    </button>
                  )}
                </div>
                <span className={`pill ${z.status === 'zugeordnet' ? 'ok' : 'w'}`}>
                  {Math.round(z.konfidenz * 100)} %
                </span>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
